import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import ReactTooltip from "react-tooltip";
import { Formik, Form } from "formik";
import { isEqual, get } from "lodash";
import { endOfMonth, startOfMonth } from "date-fns";
import { useAuthState, useAuthDispatch } from "context";
import {
  apiFetch,
  camelToTitle,
  getMonthAbbr,
  getTotalPages,
  EXPORT_DATE_FORMATS,
} from "utils";
import Button from "components/Button";
import FormField from "components/FormField";
import Table from "components/Table";
import Pagination from "components/Pagination";
import DatePickerField from "components/DatePickerField";
import { format } from "date-fns";

const ExportProgressBar = ({ progress, status, message }) => {
  const getColor = () => {
    switch (status) {
      case "completed":
        return "bg-teal-500";
      case "failed":
        return "bg-red-500";
      case "processing":
        return "bg-blue-500";
      default:
        return "bg-gray-300";
    }
  };

  const getIcon = () => {
    switch (status) {
      case "completed":
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-teal-500 mr-2"
          />
        );
      case "failed":
        return (
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className="text-red-500 mr-2"
          />
        );
      case "processing":
        return (
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-blue-500 mr-2 animate-spin"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full mt-4 p-4 bg-white rounded-lg shadow">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {getIcon()}
          <span className="text-sm font-medium text-gray-700">
            {message || "Preparing export..."}
          </span>
        </div>
        <span className="text-sm font-medium text-gray-700">{progress}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full ${getColor()}`}
          style={{
            width: `${progress}%`,
            transition: "width 0.5s ease-out",
          }}
        ></div>
      </div>
    </div>
  );
};

const DataStatusTable = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const { coalitionId } = useParams();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const currentYear = new Date().getFullYear();
  const initialFilters = {
    search: "",
    year: String(currentYear),
  };
  const [filters, setFilters] = useState(initialFilters);
  const [loadingData, setLoadingData] = useState(false);

  const [intakesExport, setIntakesExport] = useState({
    status: null,
    progress: 0,
    message: "",
    exportId: null,
    isLoading: false,
    checkRef: useRef(null),
  });

  const [servicesExport, setServicesExport] = useState({
    status: null,
    progress: 0,
    message: "",
    exportId: null,
    isLoading: false,
    checkRef: useRef(null),
  });

  const getOrganizations = async ({
    page = 1,
    search = "",
    year = currentYear,
  }) => {
    setLoadingData(true);
    try {
      const response = await apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/organizations?page[number]=${page}&filter[active_coalition][eq]=${coalitionId}&filter[coalition_membership_status][eq]=active&include=data_entry_statuses,community_data_entry_statuses&stats[total]=count&sort=name&filter[data_entry_statuses.year][eq]=${year}&filter[community_data_entry_statuses.year][eq]=${year}${
          search ? `&filter[search]=${search}` : ""
        }`,
        includeMeta: true,
      });

      const processedData = await Promise.all(
        response.data.map(async (org) => {
          const [intakes, services] = await Promise.all([
            apiFetch({
              token,
              dispatch,
              endpoint: `/api/v1/animal_intakes?filter[organization_id][eq]=${org.id}&sort=-updated_at&page[size]=1&page[number]=0`,
            }),
            apiFetch({
              token,
              dispatch,
              endpoint: `/api/v1/animal_services?filter[organization_id][eq]=${org.id}&sort=-updated_at&page[size]=1&page[number]=0`,
            }),
          ]);

          const statusRecords = {
            data: response.included.filter(
              (inc) =>
                inc.type === "organization_data_entry_statuses" &&
                String(inc.attributes.organizationId) === org.id
            ),
            services: response.included.filter(
              (inc) =>
                inc.type === "organization_community_data_entry_statuses" &&
                String(inc.attributes.organizationId) === org.id
            ),
          };

          org.servicesStatuses = {};
          org.dataStatuses = {};
          org.serviceLatestIntake = new Date(1970);
          org.latestIntake = new Date(1970);

          if (intakes.data[0]?.attributes.updatedAt) {
            org.latestIntake = new Date(intakes.data[0].attributes.updatedAt);
          }
          if (services.data[0]?.attributes.updatedAt) {
            org.serviceLatestIntake = new Date(
              services.data[0].attributes.updatedAt
            );
          }

          for (let month = 1; month <= 12; month++) {
            org.dataStatuses[month] =
              statusRecords.data.find((rec) => rec.attributes.month === month)
                ?.attributes?.status || "unbegun";

            org.servicesStatuses[month] =
              statusRecords.services.find(
                (rec) => rec.attributes.month === month
              )?.attributes?.status || "unbegun";
          }

          return org;
        })
      );

      return { ...response, data: processedData };
    } finally {
      setLoadingData(false);
    }
  };

  const {
    data: response = { data: [], included: [] },
    error,
    isLoading,
  } = useQuery(
    ["contributorsDataStatus", { coalitionId, page, ...filters }],
    () => getOrganizations({ page, ...filters }),
    { keepPreviousData: false }
  );

  useEffect(() => {
    const totalPages = getTotalPages({
      totalCount: get(response, "meta.stats.total.count") || 0,
    });
    setTotalPages(totalPages);
  }, [response]);

  const onSubmitFilters = (values) => {
    if (!isEqual(values, filters)) {
      const yearValue =
        typeof values.year !== "string"
          ? String(values.year.getFullYear())
          : values.year;

      setFilters({ ...values, year: yearValue });
      setPage(1);
      resetExportState("intakes");
      resetExportState("services");
    }
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    resetExportState("intakes");
    resetExportState("services");
  };

  const exportDataStatus = async (exportType) => {
    const isIntakes = exportType === "dataStatuses";
    const stateKey = isIntakes ? "intakesExport" : "servicesExport";

    updateExportState(stateKey, {
      status: "processing",
      progress: 0,
      message: "Starting export...",
      isLoading: true,
      exportId: null,
    });

    try {
      const response = await apiFetch({
        token,
        dispatch,
        endpoint: "/api/v1/exports",
        method: "POST",
        body: {
          coalition_id: coalitionId,
          year: filters.year,
          status_type: exportType,
          search: filters.search,
        },
      });

      if (!response.data?.id) {
        throw new Error("Export creation failed - no ID returned");
      }

      updateExportState(stateKey, {
        exportId: response.data.id,
      });

      startExportStatusPolling(response.data.id, exportType);
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
      updateExportState(stateKey, {
        status: "failed",
        message: `Export failed: ${error.message}`,
        isLoading: false,
      });
    }
  };

  const startExportStatusPolling = (exportId, exportType) => {
    const isIntakes = exportType === "dataStatuses";
    const stateKey = isIntakes ? "intakesExport" : "servicesExport";
    const checkRef = isIntakes
      ? intakesExport.checkRef
      : servicesExport.checkRef;

    if (checkRef.current) {
      clearInterval(checkRef.current);
    }

    updateExportState(stateKey, {
      status: "processing",
      message: "Preparing export...",
      progress: 0,
    });

    checkExportStatus(exportId, exportType);

    checkRef.current = setInterval(() => {
      checkExportStatus(exportId, exportType);
    }, 2000);
  };

  const checkExportStatus = async (exportId, exportType) => {
    const isIntakes = exportType === "dataStatuses";
    const stateKey = isIntakes ? "intakesExport" : "servicesExport";

    if (!exportId) {
      toast.error("Invalid export ID");
      clearInterval(
        isIntakes
          ? intakesExport.checkRef.current
          : servicesExport.checkRef.current
      );
      return;
    }

    try {
      const response = await apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/exports/${exportId}/status`,
      });

      const { status, progress, file_ready, error_message } =
        response.data?.attributes || {};
      const safeProgress = Math.min(progress || 0, 100);

      let message = "Processing export...";
      if (safeProgress < 20) message = "Initializing export...";
      else if (safeProgress < 40) message = "Collecting data...";
      else if (safeProgress < 60) message = "Processing records...";
      else if (safeProgress < 80) message = "Compiling data...";
      else message = "Finalizing export...";

      updateExportState(stateKey, {
        status,
        progress: safeProgress,
        message,
      });

      if (file_ready) {
        clearInterval(
          isIntakes
            ? intakesExport.checkRef.current
            : servicesExport.checkRef.current
        );
        updateExportState(stateKey, {
          status: "completed",
          progress: 100,
          message: "Export ready for download!",
          isLoading: false,
        });
      } else if (status === "failed") {
        clearInterval(
          isIntakes
            ? intakesExport.checkRef.current
            : servicesExport.checkRef.current
        );
        updateExportState(stateKey, {
          status: "failed",
          message: `Export failed: ${error_message || "Unknown error"}`,
          isLoading: false,
        });
        toast.error(error_message || "Export processing failed");
      }
    } catch (error) {
      clearInterval(
        isIntakes
          ? intakesExport.checkRef.current
          : servicesExport.checkRef.current
      );
      updateExportState(stateKey, {
        status: "failed",
        message: "Error checking export status",
        isLoading: false,
      });
      toast.error("Error checking export status");
    }
  };

  const triggerDownload = async (exportId, exportType) => {
    try {
      const statusResponse = await apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/exports/${exportId}/status`,
      });

      const isReady =
        statusResponse.data?.attributes?.file_ready ||
        statusResponse.data?.attributes?.status === "completed" ||
        statusResponse.data?.status === "completed";

      if (!isReady) {
        throw new Error("The export file is not yet available for download");
      }

      const AUTH_TOKEN_KEY = "@sac-token";
      const savedToken = JSON.parse(localStorage.getItem(AUTH_TOKEN_KEY));
      if (!savedToken?.accessToken) {
        throw new Error("Authentication required");
      }

      const filename =
        exportType === "dataStatuses"
          ? "intake-outcome-data-status-table.csv"
          : "community-services-data-status-table.csv";

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      const xhr = new XMLHttpRequest();
      xhr.open("GET", `/api/v1/exports/${exportId}/download`, true);

      xhr.setRequestHeader("access-token", savedToken.accessToken);
      xhr.setRequestHeader("client", savedToken.client);
      xhr.setRequestHeader("uid", savedToken.uid);
      xhr.setRequestHeader("Accept", "text/csv");

      xhr.responseType = "blob";

      xhr.onload = function () {
        if (this.status === 200) {
          const blob = new Blob([this.response], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
        document.body.removeChild(iframe);
      };

      xhr.send();
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.message || "Failed to download file");

      const stateKey =
        exportType === "dataStatuses" ? "intakesExport" : "servicesExport";
      updateExportState(stateKey, {
        status: "failed",
        message: `Download failed: ${error.message}`,
      });

      if (
        error.message.includes("Authentication") ||
        error.message.includes("401")
      ) {
        dispatch({ type: "LOGOUT" });
      }
    }
  };

  const updateExportState = (exportKey, updates) => {
    if (exportKey === "intakesExport") {
      setIntakesExport((prev) => ({ ...prev, ...updates }));
    } else {
      setServicesExport((prev) => ({ ...prev, ...updates }));
    }
  };

  const resetExportState = (exportType) => {
    const isIntakes = exportType === "intakes";
    const stateKey = isIntakes ? "intakesExport" : "servicesExport";
    const checkRef = isIntakes
      ? intakesExport.checkRef
      : servicesExport.checkRef;

    if (checkRef.current) {
      clearInterval(checkRef.current);
    }

    updateExportState(stateKey, {
      status: null,
      progress: 0,
      message: "",
      exportId: null,
      isLoading: false,
    });
  };

  useEffect(() => {
    return () => {
      if (intakesExport.checkRef.current)
        clearInterval(intakesExport.checkRef.current);
      if (servicesExport.checkRef.current)
        clearInterval(servicesExport.checkRef.current);
    };
  }, []);

  const columns = [
    {
      accessor: "attributes.name",
      Header: "Name",
    },
    ...Array.from({ length: 12 }, (_, i) => ({
      accessor: `dataStatuses.${i + 1}`,
      Header: getMonthAbbr(i + 1),
      Cell: ({ cell, row: { original } }) => (
        <StatusIndicator status={cell.value} month={i + 1} id={original.id} />
      ),
    })),
    {
      accessor: "latestIntake",
      Header: "Last Update",
      sortType: "datetime",
      sortDescFirst: true,
      Cell: ({ cell }) => (
        <span>
          {cell.value?.toDateString() !== new Date(1970).toDateString()
            ? format(new Date(cell.value), "MM-dd-yyyy")
            : "N/A"}
        </span>
      ),
    },
  ];

  const serviceStatusColumns = [
    {
      accessor: "attributes.name",
      Header: "Name",
    },
    ...Array.from({ length: 12 }, (_, i) => ({
      accessor: `servicesStatuses.${i + 1}`,
      Header: getMonthAbbr(i + 1),
      Cell: ({ cell, row: { original } }) => (
        <StatusIndicator
          status={cell.value}
          month={i + 1}
          id={original.id}
          data="services"
        />
      ),
    })),
    {
      accessor: "serviceLatestIntake",
      Header: "Last Update",
      sortType: "datetime",
      sortDescFirst: true,
      Cell: ({ cell }) => (
        <span>
          {cell.value?.toDateString() !== new Date(1970).toDateString()
            ? format(new Date(cell.value), "MM-dd-yyyy")
            : "N/A"}
        </span>
      ),
    },
  ];

  const columnWidths = new Array(14).fill(null);
  columnWidths[0] = "w-40";
  columnWidths[13] = "w-36";

  return (
    <div className="space-y-12">
      <div>
        <h2>Intake/Outcome Data Entry Status</h2>

        <div className="sac-border mb-6">
          <Formik
            initialValues={initialFilters}
            onSubmit={onSubmitFilters}
            enableReinitialize
          >
            {({ resetForm, values }) => (
              <Form>
                <div className="items-end xl:flex xl:space-x-6">
                  <div className="mb-4 space-y-2 lg:flex lg:space-x-2 lg:space-y-0 xl:w-2/3 xl:mb-0">
                    <FormField
                      name="search"
                      label="Search"
                      placeholder="Name or EIN"
                    />
                    <DatePickerField
                      label="Export Year"
                      name="year"
                      dateFormat={EXPORT_DATE_FORMATS.YEAR}
                      showYearPicker
                      maxDate={endOfMonth(new Date())}
                      minDate={startOfMonth(new Date(2010, 1, 1))}
                      required
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button type="submit">Search</Button>
                    <Button
                      type="button"
                      onClick={() => {
                        resetForm({ values: initialFilters });
                        resetFilters();
                      }}
                      emphasis="transparent"
                    >
                      Reset Filters
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        <Table
          data={response.data?.length > 0 ? response.data : []}
          columns={columns}
          columnWidths={columnWidths}
          noResultsText="No contributors found"
          isLoading={isLoading || loadingData}
          error={error}
        />

        {intakesExport.status && (
          <ExportProgressBar
            progress={intakesExport.progress}
            status={intakesExport.status}
            message={intakesExport.message}
          />
        )}

        <Button
          onClick={() =>
            intakesExport.status === "completed"
              ? triggerDownload(intakesExport.exportId, "dataStatuses")
              : exportDataStatus("dataStatuses")
          }
          isLoading={intakesExport.status === "processing"}
          loadingText={`Generating (${intakesExport.progress}%)`}
          emphasis="secondary"
          className="w-max mt-4"
          disabled={
            intakesExport.status === "processing" || isLoading || loadingData
          }
        >
          {intakesExport.status === "completed"
            ? "Download Intake Data Status CSV"
            : "Generate Intakes Data Status CSV"}
        </Button>

        {!!response.data?.length && (
          <div className="flex justify-end mt-8">
            <Pagination
              totalPages={totalPages}
              currentPage={page}
              onPageChanged={setPage}
            />
          </div>
        )}
      </div>

      <div className="mt-12">
        <h2>Community Services Data Entry Status</h2>

        <Table
          data={response.data?.length > 0 ? response.data : []}
          columns={serviceStatusColumns}
          columnWidths={columnWidths}
          noResultsText="No contributors found"
          isLoading={isLoading || loadingData}
          error={error}
        />

        {servicesExport.status && (
          <ExportProgressBar
            progress={servicesExport.progress}
            status={servicesExport.status}
            message={servicesExport.message}
          />
        )}

        <Button
          onClick={() =>
            servicesExport.status === "completed"
              ? triggerDownload(servicesExport.exportId, "servicesStatuses")
              : exportDataStatus("servicesStatuses")
          }
          isLoading={servicesExport.status === "processing"}
          loadingText={`Generating (${servicesExport.progress}%)`}
          emphasis="secondary"
          className="w-max mt-4"
          disabled={
            servicesExport.status === "processing" || isLoading || loadingData
          }
        >
          {servicesExport.status === "completed"
            ? "Download Community Services Data Status CSV"
            : "Generate Community Services Data Status CSV"}
        </Button>

        {!!response.data?.length && (
          <div className="flex justify-end mt-8">
            <Pagination
              totalPages={totalPages}
              currentPage={page}
              onPageChanged={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const StatusIndicator = ({ status, month, id, data = "intakes" }) => {
  const iconMap = {
    unbegun: { icon: faCircle, color: "text-dashboard-medium-gray" },
    incomplete: { icon: faExclamationCircle, color: "text-gold" },
    complete: { icon: faCheckCircle, color: "text-teal" },
  };

  const { icon, color } = iconMap[status] || { icon: null, color: "" };

  if (!icon) return null;

  const tooltipId = `${data}_${month}_${id}`;

  return (
    <>
      <span data-tip data-for={tooltipId}>
        <FontAwesomeIcon icon={icon} className={`${color} text-2xl`} />
      </span>
      <ReactTooltip place="right" className="w-48" id={tooltipId}>
        {camelToTitle(status) || "Unavailable"}
      </ReactTooltip>
    </>
  );
};

export default DataStatusTable;
